import {
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { Unsubscribe } from 'redux'

import firestore from './firebase-db'
import { SessionMap } from './types'

const QR_AUTH_KEY_COLLECTION = 'qr-auth-key'
const DEVSUITE_COLLECTION = 'devsuite'

const useFirebaseQRAuthService = (key: string) => {
  const [qrAuthData, setQRAuthData] = useState<any>()

  useEffect(() => {
    if (!key) {
      setQRAuthData(undefined)
    }

    let unsub: Unsubscribe

    if (key) {
      const docRef = doc(firestore, QR_AUTH_KEY_COLLECTION, key)
      unsub = onSnapshot(docRef, (doc) => {
        if (!doc.exists()) {
          setQRAuthData(undefined)
        }
        setQRAuthData(doc.data())
      })
    }

    return () => {
      if (key && unsub) {
        unsub()
      }
    }
  }, [key])

  const getDocument = async (documentId: string) => {
    const docRef = doc(firestore, QR_AUTH_KEY_COLLECTION, documentId)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return docSnap.data()
    }

    return undefined
  }

  const createDocumentIfNotExists = async (key: string, data: any) => {
    const docRef = doc(firestore, QR_AUTH_KEY_COLLECTION, key)
    const docSnap = await getDoc(docRef)
    const exists = docSnap.exists()

    if (exists) {
      return true
    }

    try {
      await setDoc(doc(firestore, QR_AUTH_KEY_COLLECTION, key), data)
      return true
    } catch (error) {
      console.error(
        'FirebaseQRAuthService::createDocumentIfNotExists encountered an error'
      )
      return false
    }
  }

  const updateDocument = async (documentId: string, data: any) => {
    try {
      await updateDoc(doc(firestore, QR_AUTH_KEY_COLLECTION, documentId), data)
      return true
    } catch (error) {
      console.error('updateDocument error', error)
      return false
    }
  }

  const deleteDocument = async (documentId: string) => {
    try {
      await deleteDoc(doc(firestore, QR_AUTH_KEY_COLLECTION, documentId))
      return true
    } catch (error) {
      console.error('deleteDocument error', error)
      return false
    }
  }

  const getMap = async () => {
    if (!firestore) {
      return undefined
    }
    const docRef = doc(firestore, 'session-base', 'map')
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return undefined
    }
    return docSnap.data() as SessionMap
  }

  const doesSessionExist = async ({
    projectName,
    sessionId,
  }: {
    projectName: string
    sessionId: string
  }) => {
    if (!firestore) {
      return false
    }
    const docRef = doc(
      firestore,
      DEVSUITE_COLLECTION,
      projectName,
      'session',
      sessionId
    )
    const docSnap = await getDoc(docRef)
    return docSnap.exists()
  }

  const createSession = async ({
    projectName,
    sessionId,
  }: {
    projectName: string
    sessionId: string
  }) => {
    if (!firestore) {
      return false
    }
    const exist = await doesSessionExist({ projectName, sessionId })
    const map = await getMap()
    if (exist || !map) {
      return false
    }
    await setDoc(
      doc(firestore, DEVSUITE_COLLECTION, projectName, 'session', sessionId),
      map
    ).catch(() => console.error('createSession encountered an error'))
    return true
  }

  const updateConnection = async ({
    connected,
    projectId,
    sessionId,
  }: {
    connected: boolean
    projectId: string
    sessionId: string
  }) => {
    if (!firestore) {
      console.error('Firestore is not initialized. Skipping connection update.')
      return
    }

    if (!sessionId) {
      console.error('Session ID is not provided. Skipping connection update.')
      return
    }

    try {
      const sessionExist = await doesSessionExist({
        projectName: projectId,
        sessionId,
      })

      if (!sessionExist) {
        console.error(
          `Session does not exist for project '${projectId}' and session '${sessionId}'.`
        )
        return
      }

      const docRef = doc(
        firestore,
        DEVSUITE_COLLECTION,
        projectId,
        'session',
        sessionId
      )

      await updateDoc(docRef, {
        connected,
      })
    } catch (error) {
      console.error(
        `Failed to update connection status for session '${sessionId}' in project '${projectId}'.`,
        error
      )
    }
  }

  return {
    qrAuthData,
    createDocumentIfNotExists,
    updateDocument,
    deleteDocument,
    createSession,
    doesSessionExist,
    updateConnection,
    getDocument,
  }
}

export default useFirebaseQRAuthService
